import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, Subject, Subscription, combineLatest, forkJoin, switchMap } from 'rxjs';
import { AmountFormatPipe } from 'src/app/shared/Utils/amount-format-pipe.pipe';
import { TransformDataTablePipe } from 'src/app/shared/Utils/transform-data-table.pipe';
import { TableEvent } from 'src/app/shared/components/table/Itable';
import { IPagination } from 'src/app/shared/models/pagination';
import { SharedModule } from 'src/app/shared/shared.module';
import { TableOption } from '../../interfaces/Iip-table-options';
import FinSupplierAccountRow from '../../interfaces/fin-supplier-account-row';
import ContractRow from '../../interfaces/ip-contract-row';
import IpErrorResponse from '../../interfaces/ip-error-response';
import FilterValues from '../../interfaces/ip-filter-values';
import InvoiceRow from '../../interfaces/ip-invoice-row';
import Pageable from '../../interfaces/ip-pageable';
import PurchaseOrderRow from '../../interfaces/ip-purchase-order-row';
import RenditionRow from '../../interfaces/ip-rendition-row';
import SettlementRow from '../../interfaces/ip-settlement-row';
import SupplierResponse from '../../interfaces/ip-supplier-response';
import SupplierRow from '../../interfaces/ip-supplier-row';
import { FnFiltersModalComponent } from '../../module/finance/components/fn-filters-modal/fn-filters-modal.component';
import { FnSearchInputComponent } from '../../module/finance/components/fn-search-input/fn-search-input.component';
import { FnSuppPaymentsService } from '../../module/finance/services/fn-supp-payments.service';
import { FnAccountFinanceService } from '../../services/fn-account-finance.service';
import { IpActiveContractService } from '../../services/ip-active-contract.service';
import { IpCurrencyService } from '../../services/ip-currency.service';
import { IpEnterpriseUserService } from '../../services/ip-enterprise-user.service';
import { IpPurchaseOrderService } from '../../services/ip-purchase-order.service';
import { IpRenditionService } from '../../services/ip-rendition.service';
import { IpRoleService } from '../../services/ip-role.service';
import { IpSettlementService } from '../../services/ip-settlement.service';
import { IpSnackbarService } from '../../services/ip-snackbar.service';
import { IpSubsidiaryService } from '../../services/ip-subsidiary.service';
import { IpSupplierService } from '../../services/ip-supplier.service';
import { IpTableBillService } from '../../services/ip-table-bill.service';
import { IpTableSurrenderService } from '../../services/ip-table-surrender.service';
import { TableService } from '../../types/ip-table-service';
import { IpFiltersComponent } from '../ip-filters/ip-filters.component';
import { IpSearchInputComponent } from '../ip-search-input/ip-search-input.component';
import { FnConsolidatedService } from '../../module/finance/services/fn-consoldate.service';
import { CustomDatePipe } from 'src/app/shared/Utils/pipeCustomDate';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { MatDrawerMode } from '@angular/material/sidenav';
import { IpPayInService } from '../../services/ip-pay-in.service';
import { ResponseData } from '../../interfaces/ip-response-data';
import UserResponse from '../../interfaces/ip-user-response';
import { PageResponse } from '../../interfaces/ip-page-response';
import { environment } from 'src/environments/environment';
import { CardConfig } from 'src/app/shared/models/mobile-table';
import { FilterCacheService } from 'src/app/shared/services/filter-cache.service';

@Component({
  standalone: true,
  imports: [
    SharedModule,
    IpSearchInputComponent,
    IpFiltersComponent,
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    FnSearchInputComponent,
  ],
  selector: 'app-ip-table',
  templateUrl: './ip-table.component.html',
  styleUrls: ['./ip-table.component.scss'],
  providers: [CustomDatePipe],
})
export class IpTableComponent implements OnInit, OnDestroy, OnChanges {
  @Input() noData: string = '';
  @Input() Date: string = '';
  @Input() supplierId!: number | undefined;
  @Input() propertyOrder: string[] = [];
  @Input() tableTitle!: string;
  @Input() pageItems!: number;
  @Input() tableOption!: TableOption;
  @Input() service: TableService = '';
  @Input() dataChange!: Observable<void>;
  @Input() onlyLast30days: boolean = false;
  @Input() ignoredTypeStatus?: number;
  @Input() billed?: boolean | undefined = false;
  @Input() optionalTitle?: string;
  @Input() dataOptional?: any;
  @Input() chargeNewDate?: any;
  @Input() paidOrders?: boolean;
  @Input() scroll = false;
  @Input() searchField = true;
  @Input() payInConciliated = false;
  @Input() noTopOptions = false;
  @Input() loadedData?: any;
  @Input() changeWidth = 768;
  @Input() isForSelectItem = false;
  @Output() selectedItemsChange = new EventEmitter<any>();
  @Output() selectedAllItems = new EventEmitter<boolean>();
  @Input() onlyTable: boolean = false;

  @ViewChild('modalFilters') dialogTemplate!: TemplateRef<any>;
  dialogFiltersRef!: MatDialogRef<any>;
  @Input() shouldClear = false;
  
  rangeFiltersMonth = environment.rangeFiltersMonth
  todayDate!: string;
  maxDate!: string;
  minEndDate!: string;
  maxEndDate!: string;
  filterForm!: FormGroup;
  
  // Filter menu - Logic
  @Input() filter: boolean = false;
  showFilter: boolean = false;
  filterValues: FilterValues = {};
  @Input() moreFilterValues: FilterValues = {}
  @Input() responseData?: ResponseData
  @Input() searchValueInput?: string
  @Input() filterKey?: string;
  @Input() filtersForPayIn: boolean = false;

  get creationDate() { return this.filterForm.get('creationDate') as FormControl;}
  get dueDate() { return this.filterForm.get('dueDate') as FormControl;}
  get originOwner() { return this.filterForm.get('originOwner') as FormControl; }
  get originBank() { return this.filterForm.get('originBank') as FormControl; }

  type!: 'business' | 'employee' | 'provider';
  data?: any[];
  sortOrder: string = '';
  sortFieldName: string = '';
  pagination!: IPagination;
  totalItems!: number;
  dataChangeSubscription!: Subscription;
  typeStatus: string | Function = '';
  statusCode: string | Function = '';
  currentPage: number = 0;
  test: TableService = 'invoice';
  subscription?: Subscription;
  mode?: MatDrawerMode;
  cardConfig?: CardConfig;

  private destroy$ = new Subject<void>();

  constructor(
    private roleService: IpRoleService,
    private tableBillService: IpTableBillService,
    private ipActiveContractService: IpActiveContractService,
    private ipPurchaseOrderService: IpPurchaseOrderService,
    private tableSurrender: IpTableSurrenderService,
    private ipSupplierService: IpSupplierService,
    private ipSubsidiaryService: IpSubsidiaryService,
    private ipEnterpriseUserService: IpEnterpriseUserService,
    private ipRenditionService: IpRenditionService,
    private ipSnackbarService: IpSnackbarService,
    private ipCurrencyService: IpCurrencyService,
    private customDate: CustomDatePipe,
    private transformDataTablePipe: TransformDataTablePipe,
    private amountFormatPipe: AmountFormatPipe,
    private ipSettlementService: IpSettlementService,
    private fnAccountFinanceService: FnAccountFinanceService,
    private fnPaymentsFinanceService: FnSuppPaymentsService,
    private consolidatedService: FnConsolidatedService,
    private payInService: IpPayInService,
    private route: ActivatedRoute,
    private router: Router,
    public dialog: MatDialog,
    private fb: FormBuilder,
    private translateService: TranslateService,
    private readonly filterCacheService: FilterCacheService
  ) {}

  ngOnInit(): void {
    this.updateMode(window.innerWidth)
    this.type = this.roleService.getType();
    this.setDatesStart();

    this.pagination = {
      page: 0,
      size: this.pageItems,
    };
    if (this.dataChange) {
      this.dataChangeSubscription = this.dataChange.subscribe({
        error: console.log,
        next: () => { this.executeService(); }
      });
    }

    if (this.onlyLast30days) {
      const date = new Date();
      this.filterValues.createDateTo = date
        .toISOString()
        .slice(0, 10)
        .concat('T23:59:59');
      this.filterValues.createDateFrom = new Date(
        date.setDate(date.getDate() - 30)
      )
        .toISOString()
        .slice(0, 10)
        .concat('T00:00:00');
    }
    // FORM
    this.filterForm = this.fb.group({
      creationDate: [''],
      dueDate: [''],
      originOwner: [''],
      originBank: [''],
    });
    if (this.service === 'invoice-paid' || this.service === 'surrenders-paid') {
      this.typeStatus = this.tableOption.selects![0][1];
    } else if (this.service === 'settlements') {
      this.statusCode = this.tableOption.selects![0][1];
    }
    this.loadFilterFromCache();
    this.executeService();
  }

  
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['moreFilterValues'] && !changes['moreFilterValues'].firstChange) {
      this.pagination = {
        page: 0,
        size: this.pageItems,
      };
      this.currentPage = 0;
      this.executeService()
    }
    if (changes['searchValueInput'] && !changes['searchValueInput'].firstChange) {
      this.pagination = {
        page: 0,
        size: this.pageItems,
      };
      this.currentPage = 0;
      this.executeService()
    }
    if (changes['responseData'] && !changes['responseData'].firstChange) {
      if (this.responseData){
        switch (this.responseData.type) {
          case 'edit':
            if (this.data){
              this.data = this.data.map(d=> d.id === this.responseData?.data.id ? this.responseData?.data : d)
            }
            break;
          case 'delete':
            if (this.data){
              this.data = this.data.filter(d=>d.id !== this.responseData?.data.id)
            }
            break;
          case 'add':
            if (this.data){
              this.data.push(this.responseData.data)
            }
            break;
          case 'reset':
            this.executeService()    
            break; 
          default:
            break;
        }
      }
    }
  }

  setDatesStart(){
    const today = new Date();
    this.todayDate = this.formatDateArg(today);
    this.maxEndDate = this.todayDate
    this.maxDate = this.todayDate
  }

  onChangeStatus(value: boolean) {
    if (value) {
      this.executeService();
    }
  }

  ngOnDestroy() {
    if (this.dataChangeSubscription) {
      this.dataChangeSubscription.unsubscribe();
    }
    this.subscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
    this.service = '';
  }

  // Table sorting - Logic
  onSortTable = (event: TableEvent) => {
    this.currentPage = 0;
    this.pagination.page = 0;
    if (event.event == 'clean') {
      this.sortOrder = '';
      this.sortFieldName = '';
    } else {
      this.sortOrder = event.event;
      this.sortFieldName = event.key ?? '';
    }
    this.executeService();
  };

  // Table action - Logic
  @Output() action: EventEmitter<TableEvent> = new EventEmitter<TableEvent>();

  onActionTable = (event: TableEvent) =>{
    this.action.emit(event);
  }

  checkValue(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const labelElement = inputElement.nextElementSibling as HTMLElement;

    if (inputElement.value) {
      labelElement.style.display = 'none';
    } else {
      labelElement.style.display = 'block';
    }
  }

  // Table selects - Logic
  currentSelect: number = 0;
  toggleSelect = (
    selectPosition: number,
    select: [string, string | Function]
  ) => {
    this.currentSelect = selectPosition;
    this.typeStatus = select[1];
    this.statusCode = select[1];
    this.currentPage = 0;
    this.pagination.page = 0;
    // Search reset
    this.searchValue = '';
    this.executeService();
  };

  // Search - Logic
  searchValue: string = '';

  onSearch(searchValue: string) {
    this.searchValue = searchValue;
    this.executeService();
  }

  onPageChange(page: number): void {
    this.currentPage = page - 1;
    this.pagination.page = this.currentPage;
    this.executeService();
  }


  toggleFilter = (state: boolean) => (this.showFilter = state);

  setFilters = (event?: any) => {
    this.filterValues.amountFrom = event.amountFrom;
    this.filterValues.amountTo = event.amountTo;
    this.filterValues.createDateFrom = event.dateFrom;
    // Creation date filters cannot be altered on this case
    if (!this.onlyLast30days) {
      this.filterValues.createDateFrom = event.dateFrom;
      this.filterValues.createDateTo = event.dateTo;
    }

    this.executeService();
  };

  // onStartDateChange() {
  //   if (this.creationDate) {
  //     const startDate = new Date(this.creationDate.value);

  //     this.minEndDate = startDate.toISOString().split('T')[0];
  //   } else {
  //     this.dueDate?.setValue('');
  //   }
  // }

  onStartDateChange() {
   if (this.creationDate.value == '') {
      this.dueDate.setValue('')
      this.dueDate.disable()
    } else{
      const startDate = new Date(this.creationDate.value);
      this.filterValues.createDateFrom = this.creationDate.value
      this.minEndDate = startDate.toISOString().split('T')[0] 
      const dateToday = new Date();
      let maxDate = new Date();
      maxDate = new Date(startDate.setMonth(startDate.getMonth() + this.rangeFiltersMonth))
      if (this.dueDate.value !== '' 
        && (this.dueDate.value > this.formatDateArg(maxDate)
          || this.dueDate.value < this.creationDate.value)) this.dueDate.setValue('')
      this.dueDate.enable()  
      if (maxDate > dateToday) this.maxEndDate = this.todayDate
      else this.maxEndDate = maxDate.toISOString().split('T')[0]
    }
  }
  
  onEndDateChange() {
    if (this.dueDate) {
      const endDate = new Date(this.dueDate.value);
      this.filterValues.createDateTo = this.dueDate.value
      //this.maxDate = endDate.toISOString().split('T')[0];
    } else {
      this.creationDate?.setValue('');
    }
  }

  openCalendar(DateInput: HTMLInputElement): void {
    setTimeout(() => {
      DateInput.click();
    }, 100);
  }

  // Service calls
  executeService(searchValue?: string) {
    if ( this.creationDate?.value === '' && this.dueDate?.value === ''){
      const today = new Date();          
      this.creationDate?.patchValue(this.formatDate(this.firstDay(today)))
      this.dueDate?.patchValue(this.formatDate(today))
      this.filterValues.createDateFrom = this.creationDate.value
      this.filterValues.createDateTo = this.dueDate.value
    }
    this.filterValues.originOwner = this.originOwner?.value ?? '';
    this.filterValues.originBank = this.originBank?.value ?? '';
    if (this.filterKey && this.filterValues.createDateFrom){
      this.emitFilter(this.filterValues)
    }
    const pageable: Pageable = { ...this.pagination };
    if (this.sortFieldName && this.sortOrder) {
      pageable.sort = `${this.sortFieldName},${this.sortOrder}`;
    }
    let searchName = '';
    this.onClose()
    switch (this.service) {
      case 'supplier-accounts':
        combineLatest({
          currencies: this.ipCurrencyService.currencies$,
          supplierAccounts: this.fnAccountFinanceService.getSupplierAccounts(
            this.pagination,
            this.searchValue
          ),
        }).subscribe({
          next: ({ currencies, supplierAccounts }) => {
            if (currencies) {
              const newData: Array<FinSupplierAccountRow> = [];
              supplierAccounts.content.forEach((supplierAccount) => {
                const supplierAccountRow: FinSupplierAccountRow = {
                  ...supplierAccount,
                  formattedCardBalance: supplierAccount.cardBalance.toString(),
                  formattedAccountBalance:
                    supplierAccount.accountBalance.toString(),
                };
                const foundCurrency = currencies.find((currency) => {
                  // TODO: GET real currency
                  return currency.code === 'ARS';
                });
                if (foundCurrency) {
                  const formattedCardBalance = this.amountFormatPipe.transform(
                    supplierAccount.cardBalance,
                    foundCurrency.isCommaFloat,
                    foundCurrency.symbol
                    // foundCurrency.code
                  );
                  const formattedAccountBalance =
                    this.amountFormatPipe.transform(
                      supplierAccount.accountBalance,
                      foundCurrency.isCommaFloat,
                      foundCurrency.symbol
                      // foundCurrency.code
                    );
                  supplierAccountRow.formattedCardBalance =
                    formattedCardBalance;
                  supplierAccountRow.formattedAccountBalance =
                    formattedAccountBalance;
                }
                newData.push(supplierAccountRow);
              });
              this.data = newData;
              this.totalItems = supplierAccounts.totalElements;
            }
          },
          error: (e: IpErrorResponse) => {
            this.ipSnackbarService.showErrorMessage(e);
          },
        });
        break;
      case 'account-transactions':
        this.fnAccountFinanceService
          .getAccountMovements(this.pagination, '', '', this.supplierId)
          .subscribe({
            next: (data: any) => {
              this.data = data.content;
              this.totalItems = data.totalElements;
            },
            error: (e: IpErrorResponse) => {
              this.ipSnackbarService.showErrorMessage(e);
            },
          });
        break;
      case 'card-transactions':
        this.fnAccountFinanceService
          .getCardMovements(this.pagination, '', '', this.supplierId)
          .subscribe({
            next: (data: any) => {
              this.data = data.content;
              this.totalItems = data.totalElements;
            },
            error: (e: IpErrorResponse) => {
              this.ipSnackbarService.showErrorMessage(e);
            },
          });
        break;
      case 'supplier-users':
        this.ipSupplierService.getSupplierUsers(pageable).subscribe({
          next: (data: any) => {
            this.data = data.content;
            this.totalItems = data.totalElements;
          },
          error: (e: IpErrorResponse) => {
            this.ipSnackbarService.showErrorMessage(e);
          },
        });
        break;
      case 'consolidated':
        if ( this.creationDate?.value === '' && this.dueDate?.value === ''){
          const today = new Date();          
          this.creationDate?.patchValue(this.formatDate(this.firstDay(today)))
          this.dueDate?.patchValue(this.formatDate(today))
        }
        
        this.consolidatedService
          .getConsolidates(
            this.pagination,
            this.creationDate!.value
              ? this.creationDate!.value + 'T00:00:00'
              : '',
            this.dueDate!.value ? this.dueDate!.value + 'T23:59:59' : '',
            searchValue ?? '',
            this.paidOrders 
          )
          .subscribe({
            next: (data: any) => {
              const dataValues: any[] = [];
              data.content.forEach((val: any) => {
                const value = {
                  datetime_generated: val.generationDate,
                  datetime_modified: this.customDate.transformDateTime(
                    this.convertToDate(val.lastModificationDate)
                  ),
                  id_consolidated: val.consolidatedId,
                  total_providers: val.totalSuppliers,
                  total_amount: val.amountToPay,
                  total_invoices: val.totalInvoices,
                  status: val.status,
                  consolidated_name: val.consolidatedName,
                  observation: val.observation,
                  createdBy: val.createdBy,
                  modifiedBy: val.modifiedBy,
                };
                dataValues.push(value);
              });
              this.data = dataValues;
              this.totalItems = data.totalElements;
            },
          });
        break;
      case 'consolidated-pay-in':
        this.cardConfig = {
          headerKey: 'originOwner',
          fields: [
            { 
              label: this.translateService.instant('IP.TABLE.CONSOLIDATED.PAY_IN.DATACONCEPT'),
              key: 'dataConcept',
            },
            { 
              label: this.translateService.instant('IP.TABLE.CONSOLIDATED.PAY_IN.DATEPAYIN'),
              key: 'datePayIn',
              isDate: true,
            },
            { 
              label: this.translateService.instant('IP.TABLE.CONSOLIDATED.PAY_IN.ORIGINBANK'),
              key: 'originBank',
            },
            { 
              label: this.translateService.instant('IP.TABLE.CONSOLIDATED.PAY_IN.AMOUNT'),
              key: 'amount', 
              bold: true,     
            },
          ],
          showActionButton: true,
          actions: this.tableOption.actions,
        };
        
        this.payInService
          .getPayIn(
            this.pagination,
            this.creationDate!.value
              ? this.creationDate!.value + 'T00:00:00'
              : '',
            this.dueDate!.value ? this.dueDate!.value + 'T23:59:59' : '',
            this.payInConciliated,
            this.originOwner?.value !== '' ? this.originOwner?.value : undefined,
            this.originBank?.value !== '' ? this.originBank?.value : undefined
          )
          .subscribe({
            next: (data: any) => {
              const dataValues: any[] = [];
              data.content.forEach((val: any) => {
                const value = val
                value.datePayIn = this.customDate.transformDateTime(
                  this.convertToDate(val.date))
                value.conciliationDate = val.conciliationDate;
                value.dataConcept = val.concept
                value.totalAmount = val.amount
                value.amount = val.currency + ' ' + val.amount
                value.insurancePolicyName = val.insurancePolicyName;
                value.productName = val.productName;
                dataValues.push(value);
              });
              this.data = dataValues;
              this.totalItems = data.totalElements;
            },
          });
        break;
      case 'pending-pay-in':
        this.cardConfig = {
          headerKey: 'originOwner',
          fields: [
            { 
              label: this.translateService.instant('IP.TABLE.CONSOLIDATED.PAY_IN.DATACONCEPT'),
              key: 'dataConcept',
            },
            { 
              label: this.translateService.instant('IP.TABLE.CONSOLIDATED.PAY_IN.DATEPAYIN'),
              key: 'datePayIn',
              isDate: true,
            },
            { 
              label: this.translateService.instant('IP.TABLE.CONSOLIDATED.PAY_IN.ORIGINBANK'),
              key: 'originBank',
            },
            { 
              label: this.translateService.instant('IP.TABLE.CONSOLIDATED.PAY_IN.AMOUNT'),
              key: 'amount', 
              bold: true,     
            },
          ],
          showActionButton: true,
          actions: this.tableOption.actions,
        };
        
        this.payInService
          .getPaymentsToConciliate(
            undefined,
            undefined,
            this.payInConciliated,
            undefined,
            undefined
          )
          .subscribe({
            next: (data: any) => {
              const dataValues: any[] = [];
              data.forEach((val: any) => {
                const value = val
                value.datePayIn = this.customDate.transformDateTime(
                  this.convertToDate(val.date))
                value.conciliationDate = val.conciliationDate;
                value.dataConcept = val.concept
                value.totalAmount = val.amount
                value.amount = val.currency + ' ' + val.amount
                value.insurancePolicyName = val.insurancePolicyName;
                value.productName = val.productName;
                dataValues.push(value);
              });
              this.data = dataValues;
            },
          });
        break;
      case 'payments-done':
        this.fnPaymentsFinanceService
          .getPaymentsDone(
            this.pagination,
            this.creationDate!.value
              ? this.creationDate!.value + 'T00:00:00'
              : '',
            this.dueDate!.value ? this.dueDate!.value + 'T00:00:00' : '',
            searchValue ?? ''
          )
          .subscribe({
            next: (data: any) => {
              const dataValues: any[] = [];
              data.content.forEach((val: any) => {
                const value = {
                  id: val.id,
                  provider_name: val.supplierName,
                  idConsolidate: val.consolidatedId,
                  date: this.transformDataTablePipe.transform(
                    val.dateTime,
                    'Date',
                    ''
                  ),
                  amount: val.amount,
                  accNumber: val.accountNumber,
                };
                dataValues.push(value);
              });
              this.data = dataValues;
              this.totalItems = data.totalElements;
            },
            error: (e: IpErrorResponse) => {
              this.ipSnackbarService.showErrorMessage(e);
            },
          });
        break;
      case 'payments-ongoing':
        this.fnPaymentsFinanceService
          .getPaymentsSend(
            this.pagination,
            this.creationDate!.value
              ? this.creationDate!.value + 'T00:00:00'
              : '',
            this.dueDate!.value ? this.dueDate!.value + 'T00:00:00' : '',
            searchValue ?? ''
          )
          .subscribe({
            next: (data: any) => {
              const dataValues: any[] = [];
              data.content.forEach((val: any) => {
                const value = {
                  id: val.id,
                  provider_name: val.supplierName,
                  idConsolidate: val.consolidatedId,
                  date: this.transformDataTablePipe.transform(
                    val.dateTime,
                    'Date',
                    ''
                  ),
                  amount: val.amount,
                  accNumber: val.accountNumber,
                };
                dataValues.push(value);
              });
              this.data = dataValues;
              this.totalItems = data.totalElements;
            },
            error: (e: IpErrorResponse) => {
              this.ipSnackbarService.showErrorMessage(e);
            },
          });
        break;
      case 'subsidiaries':
        this.ipSubsidiaryService.getSubsidiaries(pageable).subscribe({
          next: (response) => {
            this.data = response.content;

            this.totalItems = response.totalElements;
          },
          error: (e: IpErrorResponse) => {
            this.ipSnackbarService.showErrorMessage(e);
          },
        });
        break;
      case 'user-management':
        this.cardConfig = {
          headerKey: 'username',
          fields: [
            { 
              label: this.translateService.instant('IP.ADMIN_TABLE.USERSTATUS'),
              key: 'userStatus', 
              primaryColorText: true,
              mapLabels: {
                'ACTIVATED': this.translateService.instant('IP.USER_FORM_MODAL.USER_STATE.ACTIVATED'),
                'DISABLED': this.translateService.instant('IP.USER_FORM_MODAL.USER_STATE.DISABLED'),
                'INACTIVE': this.translateService.instant('IP.USER_FORM_MODAL.USER_STATE.INACTIVE'),
              }
            },
            { 
              label: this.translateService.instant('IP.ADMIN_TABLE.EMAIL'),
              key: 'email',
            },
            { 
              label: this.translateService.instant('IP.ADMIN_TABLE.ROLE'),
              key: 'role',              
            },
          ],
          showActionButton: true,
          actions: this.tableOption.actions,
        };
        this.ipEnterpriseUserService.getUsers(pageable).subscribe({
          next: (data) => {
            this.data = data.content;
            this.totalItems = data.totalElements;
          },
          error: (e: IpErrorResponse) => {
            this.ipSnackbarService.showErrorMessage(e);
          },
        });
        break;
      case 'admin-management':
        this.ipEnterpriseUserService.getAdmins(pageable).subscribe({
          next: (data) => {
            this.data = data.content;            
            this.totalItems = data.totalElements;
          },
          error: (e: IpErrorResponse) => {
            this.ipSnackbarService.showErrorMessage(e);
          },
        });
        break;
      case 'employee-management':
        this.ipEnterpriseUserService.getEmployees(pageable).subscribe({
          next: (data) => {
            this.data = data.content;
            this.totalItems = data.totalElements;
          },
          error: (e: IpErrorResponse) => {
            this.ipSnackbarService.showErrorMessage(e);
          },
        });
        break;
      case 'supplier':
        searchName = 'businessName';
        this.ipSupplierService
          .getSupplierManagers(pageable, searchName, this.searchValue)
          .subscribe({
            next: (data) => {
              const suppliers: SupplierResponse[] = data.content;
              const supplierRows: SupplierRow[] = [];
              suppliers.forEach((supplier) => {
                supplier.userInvopay.forEach((supplierUser) => {
                  const newSupplierUser: SupplierRow = {
                    ...supplierUser,
                    id: supplier.id,
                    businessName: supplier.businessName,
                    countryId: supplier.country?.id,
                    countryName: supplier.country?.name,
                    state: !supplier.deleted
                  };
                  supplierRows.push(newSupplierUser);
                });
              });
              this.data = supplierRows;
              this.totalItems = data.totalElements;
            },
            error: (e: IpErrorResponse) => {
              this.ipSnackbarService.showErrorMessage(e);
            },
          });
        break;
        case 'supplier-broker':
          searchName = 'businessName';
          this.ipSupplierService
            .getSupplierBrokerManagers(pageable, searchName, this.searchValue)
            .subscribe({
              next: (data) => {
                const suppliers: SupplierResponse[] = data.content;
                const supplierRows: SupplierRow[] = [];
                suppliers.forEach((supplier) => {
                  supplier.userInvopay.forEach((supplierUser) => {
                    const newSupplierUser: SupplierRow = {
                      ...supplierUser,
                      id: supplier.id,
                      businessName: supplier.businessName,
                      countryId: supplier.country?.id,
                      countryName: supplier.country?.name,
                      state: !supplier.deleted
                    };
                    supplierRows.push(newSupplierUser);
                  });
                });
                this.data = supplierRows;
                this.totalItems = data.totalElements;
              },
              error: (e: IpErrorResponse) => {
                this.ipSnackbarService.showErrorMessage(e);
              },
            });
          break;
      case 'invoice':
      case 'invoice-report':
      case 'invoice-paid':
        this.cardConfig = {
          headerKey: 'fiscalId',
          headerLabel: '#',
          fields: [
            { 
              label: this.translateService.instant('IP.CARD_TABLE.INVOICE.STATUS'),
              key: 'status', 
              primaryColorText: true
            },
            { 
              label: this.translateService.instant('IP.CARD_TABLE.INVOICE.LOADINGDATE'),
              key: 'loadingDate',
              isDate: true
            },
            { 
              label: this.translateService.instant('IP.CARD_TABLE.INVOICE.CREATIONDATE'),
              key: 'creationDate',
              isDate: true
            },
            { 
              label: this.translateService.instant('IP.CARD_TABLE.INVOICE.DUEDATE'),
              key: 'dueDate',
              isDate: true
            },
            { label: this.translateService.instant('IP.CARD_TABLE.INVOICE.AMOUNT'), key: 'totalAmount', isAmount: true, currencyCode: 'currencyCode', bold: true },
          ],
          showActionButton: true,
          actions: this.tableOption.actions,
          isActionDisabled: (item, action) => {
            if (action === 'pay' && item.typeStatus?.isPayable == false) {
              return true;
            }
            return false;
          }
        };
        if (this.type === 'business') {
          searchName = 'fiscalIdOrSupplierName';
        } else {
          searchName = 'fiscalIdOrUsername';
        }
        // Sort replacements
        if (this.sortFieldName === 'supplierName') {
          this.sortFieldName = 'supplierBusinessName';
        } else if (this.sortFieldName === 'paymentDate') {
          this.sortFieldName = 'paymentProofPaymentDate';
        }
        const url = this.route.snapshot.url[0].path;
        combineLatest({
          currencies: this.ipCurrencyService.currencies$,
          invoices: this.tableBillService.getData(
            this.type,
            this.pagination,
            this.sortFieldName,
            this.sortOrder,
            searchName,
            this.searchValue,
            this.moreFilterValues,
            Number(this.typeStatus),
            this.service === 'invoice-report',
            this.ignoredTypeStatus,
            undefined,
            url === 'all-invoices'
          ),
        }).subscribe({
          next: ({ currencies, invoices }) => {
            if (currencies) {
              const newData: Array<InvoiceRow> = [];
              invoices.content.forEach((invoice) => {
                const newInvoice: InvoiceRow = {
                  ...invoice,
                  status: invoice.typeStatus.status,
                  userCreator:invoice.fullName
                };
                const foundCurrency = currencies.find((currency) => {
                  return currency.id === invoice.currencyId;
                });
                if (foundCurrency) {
                  newInvoice.currencyCode = foundCurrency.code;

                  newInvoice.formattedAmount = this.amountFormatPipe.transform(
                    invoice.totalAmount,
                    foundCurrency.isCommaFloat,
                    foundCurrency.symbol,
                    foundCurrency.code
                  );
                }
                newData.push(newInvoice);
              });
              this.data = newData;
              this.totalItems = invoices.totalElements;
            }
          },
          error: (e: IpErrorResponse) => {
            this.ipSnackbarService.showErrorMessage(e);
          },
        });
        break;
      case 'invoice-supplier':
        this.cardConfig = {
          headerKey: 'fiscalId',
          headerLabel: '#',
          fields: [
            { 
              label: this.translateService.instant('IP.CARD_TABLE.INVOICE.STATUS'),
              key: 'status', 
              primaryColorText: true
            },
            { 
              label: this.translateService.instant('IP.CARD_TABLE.INVOICE.LOADINGDATE'),
              key: 'loadingDate',
              isDate: true
            },
            { 
              label: this.translateService.instant('IP.CARD_TABLE.INVOICE.CREATIONDATE'),
              key: 'creationDate',
              isDate: true
            },
            { 
              label: this.translateService.instant('IP.CARD_TABLE.INVOICE.DUEDATE'),
              key: 'dueDate',
              isDate: true
            },
            { label: this.translateService.instant('IP.CARD_TABLE.INVOICE.AMOUNT'), key: 'totalAmount', isAmount: true, currencyCode: 'currencyCode', bold: true },
          ],
          showActionButton: true,
          actions: this.tableOption.actions,
          isActionDisabled: (item, action) => {
            if (action === 'pay' && item.typeStatus?.isPayable == false) {
              return true;
            }
            return false;
          }
        };
        this.subscription = this.route.paramMap
              .pipe(
                switchMap((params) => {
                  const selectedId = Number(params.get('id'));
                  return combineLatest({
                    currencies: this.ipCurrencyService.currencies$,
                    invoices: this.tableBillService.getData(
                      this.type,
                      this.pagination,
                      this.sortFieldName,
                      this.sortOrder,
                      searchName,
                      this.searchValue,
                      {...this.moreFilterValues, brokersId: [selectedId]},
                      Number(this.typeStatus),
                      undefined,
                      undefined,
                      undefined,
                    ),
                  })
                })
              ).subscribe({
                next: ({ currencies, invoices }) => {
                  if (currencies) {
                    const newData: Array<InvoiceRow> = [];
                    invoices.content.forEach((invoice) => {
                      const newInvoice: InvoiceRow = {
                        ...invoice,
                        status: invoice.typeStatus.status,
                      };
                      const foundCurrency = currencies.find((currency) => {
                        return currency.id === invoice.currencyId;
                      });
                      if (foundCurrency) {
                        newInvoice.currencyCode = foundCurrency.code;

                        newInvoice.formattedAmount = this.amountFormatPipe.transform(
                          invoice.totalAmount,
                          foundCurrency.isCommaFloat,
                          foundCurrency.symbol,
                          foundCurrency.code
                        );
                      }
                      newData.push(newInvoice);
                    });
                    this.data = newData;
                    this.totalItems = invoices.totalElements;
                  }
                },
                error: (e: IpErrorResponse) => {
                  this.ipSnackbarService.showErrorMessage(e);
                },
              });
        break
      case 'surrenders':
      case 'surrenders-report':
      case 'surrenders-paid':
        if (isNaN(parseInt(this.searchValue))) {
          // String search
          if (this.type === 'business') {
            searchName = 'username';
          } else if (this.type === 'employee') {
            searchName = 'concept';
          } else {
            console.error('Wrong user kind.');
          }
        } else {
          // Numeric search
          searchName = 'renditionId';
        }
        if (this.filterValues.createDateFrom && this.filterValues.createDateFrom.length == 10) {
          this.filterValues.createDateFrom = this.filterValues.createDateFrom + 'T00:00:00'
        }
        if (this.filterValues.createDateTo && this.filterValues.createDateTo.length == 10) {
          this.filterValues.createDateTo = this.filterValues.createDateTo + 'T23:59:59'
        }
        combineLatest({
          renditionTypes: this.ipRenditionService.getRenditionTypes(),
          currencies: this.ipCurrencyService.currencies$,
          renditions: this.tableSurrender.getData(
            this.type,
            this.pagination,
            this.sortFieldName,
            this.sortOrder,
            searchName,
            this.searchValue,
            this.filterValues,
            Number(this.typeStatus),
            this.service === 'surrenders-report',
            this.ignoredTypeStatus
          ),
        }).subscribe({
          next: ({ renditionTypes, currencies, renditions }) => {
            if (!currencies) {
              return;
            }
            const newData: Array<RenditionRow> = [];
            renditions.content.forEach((rendition) => {
              const newRendition: RenditionRow = {
                ...rendition,
                status: rendition.typeStatus.status,
              };
              if (rendition.renditionTypeId) {
                let foundRenditionType = renditionTypes.find(
                  (renditionType) => {
                    return renditionType.id === rendition.renditionTypeId;
                  }
                );
                if (foundRenditionType) {
                  newRendition.renditionType = foundRenditionType.name;
                }
              }
              const foundCurrency = currencies.find((currency) => {
                return currency.id === rendition.currencyId;
              });
              if (foundCurrency) {
                newRendition.currencyCode = foundCurrency.code;
                newRendition.formattedAmount = this.amountFormatPipe.transform(
                  rendition.amount,
                  foundCurrency.isCommaFloat,
                  foundCurrency.symbol,
                  foundCurrency.code
                );
              }
              newData.push(newRendition);
            });
            this.data = newData;
            this.totalItems = renditions.totalElements;
          },
          error: (e: IpErrorResponse) => {
            this.ipSnackbarService.showErrorMessage(e);
          },
        });
        break;
      case 'active-contracts':
        if (isNaN(parseInt(this.searchValue))) {
          // String search
          searchName = 'q';
        } else {
          // Numeric search
          searchName = 'contractId';
        }
        // Sort replacements
        if (this.sortFieldName === 'supplierName') {
          this.sortFieldName = 'supplierBusinessName';
        }
        forkJoin({
          suppliers: this.ipSupplierService.getSupplierManagers(),
          contracts: this.ipActiveContractService.getData(
            this.pagination,
            this.sortFieldName,
            this.sortOrder,
            searchName,
            this.searchValue
          ),
        }).subscribe({
          next: ({ suppliers, contracts }) => {
            const newData: Array<ContractRow> = [];
            contracts.content.forEach((contract) => {
              const newContract: ContractRow = {
                ...contract,
                costCenterName: contract.costCenter.name,
              };
              if (contract.supplierId) {
                let foundSupplier = suppliers.content.find((supplier) => {
                  return supplier.id === contract.supplierId;
                });
                if (foundSupplier) {
                  newContract.supplierName = foundSupplier.businessName;
                }
              }
              newData.push(newContract);
            });
            this.data = newData;
            this.totalItems = contracts.totalElements;
          },
          error: (e: IpErrorResponse) => {
            this.ipSnackbarService.showErrorMessage(e);
          },
        });
        break;
      case 'purchase-order':
        if (isNaN(parseInt(this.searchValue))) {
          // String search
          searchName = 'q';
        } else {
          // Numeric search
          searchName = 'purchaseId';
        }
        // Sort replacements
        if (this.sortFieldName === 'supplierName') {
          this.sortFieldName = 'supplierBusinessName';
        }
        forkJoin({
          suppliers: this.ipSupplierService.getSupplierManagers(),
          purchaseOrders: this.ipPurchaseOrderService.getData(
            this.pagination,
            this.sortFieldName,
            this.sortOrder,
            searchName,
            this.searchValue
          ),
        }).subscribe({
          next: ({ suppliers, purchaseOrders }) => {
            const newData: Array<PurchaseOrderRow> = [];
            purchaseOrders.content.forEach((purchaseOrder) => {
              const newPurchaseOrder: PurchaseOrderRow = {
                ...purchaseOrder,
                costCenterName: purchaseOrder.costCenter.name,
              };
              if (purchaseOrder.supplierId) {
                let foundSupplier = suppliers.content.find((supplier) => {
                  return supplier.id === purchaseOrder.supplierId;
                });
                if (foundSupplier) {
                  newPurchaseOrder.supplierName = foundSupplier.businessName;
                }
              }
              newData.push(newPurchaseOrder);
            });
            this.data = newData;
            this.totalItems = purchaseOrders.totalElements;
          },
          error: (e: IpErrorResponse) => {
            this.ipSnackbarService.showErrorMessage(e);
          },
        });
        break;
      case 'settlements':
        this.cardConfig = {
          headerKey: 'settlementId',
          headerLabel: '#',
          fields: [
            { 
              label: this.translateService.instant('IP.CARD_TABLE.SETTLEMENTS.BROKER'),
              key: 'providerName', 
            },
            { 
              label: this.translateService.instant('IP.CARD_TABLE.SETTLEMENTS.STATUS'),
              key: 'status', 
              subKey: 'statusText',
              primaryColorText: true
            },
            { 
              label: this.translateService.instant('IP.CARD_TABLE.SETTLEMENTS.CREATIONTIME'),
              key: 'creationTime',
              isDate: true
            },
            { 
              label: this.translateService.instant('IP.CARD_TABLE.SETTLEMENTS.STARTDATESETTLEMENT'),
              key: 'startDateSettlement',
              isDate: true
            },
            { 
              label: this.translateService.instant('IP.CARD_TABLE.SETTLEMENTS.ENDDATESETTLEMENT'),
              key: 'endDateSettlement',
              isDate: true
            },
            { label: this.translateService.instant('IP.CARD_TABLE.SETTLEMENTS.SETTLEMENTSUMMARY'), key: 'formattedTotalAmount', bold: true },
          ],
          showActionButton: true,
          actions: this.tableOption.actions,
          isActionDisabled: (item, action) => {
            if (action === 'delete' && item.status?.statusCode !== 'open') {
              return true;
            }
            return false;
          }
        };
        this.ipSettlementService.getData(
            this.pagination,
            this.sortFieldName,
            this.sortOrder,
            'description',
            this.searchValue,
            this.statusCode.toString(),
            this.billed,
            this.moreFilterValues
          )
          .subscribe({
          next: ( settlements ) => {
          this.ipCurrencyService.currencies$.subscribe(currencies =>{
            if (!currencies) {
              return;
            }
            const newData: Array<SettlementRow> = [];
            settlements.content.forEach((settlement) => {
              const totalCommission =
                settlement.settlementSummary.settlementCommissionAmount;
              const settlementCurrency =
                settlement.settlementSummary.settlementCurrency;        
              const foundCurrency = currencies.find((currency) => {
                return currency.code === settlementCurrency;
              });              
              const settlementCurrencyRels = settlement.settlementCurrencyRels;
              let formattedTotalAmount = 'N/A';
              if (settlementCurrencyRels && settlementCurrencyRels.length > 0) {
                formattedTotalAmount = settlementCurrencyRels.map((rel) => {
                  const currencyRel = currencies.find((currency) => currency.code === rel.currencyCode);
                    if (currencyRel) {
                      return this.amountFormatPipe.transform(
                        rel.amount,
                        currencyRel.isCommaFloat,
                        currencyRel.symbol,
                        currencyRel.code
                      );
                    } else return rel.currencyCode + ' ' + this.amountFormatPipe.transform(rel.amount)
                }).join('\n');
              } else if (foundCurrency) {
                formattedTotalAmount = this.amountFormatPipe.transform(
                  totalCommission,
                  foundCurrency.isCommaFloat,
                  foundCurrency.symbol,
                  foundCurrency.code
                );
              } else {
                formattedTotalAmount = this.amountFormatPipe.transform(
                  totalCommission,
                  true,
                  '$',
                  settlementCurrency
                );
                console.warn(
                  "The settlement's currency wasn't found in the database."
                );
              }
              newData.push({
                ...settlement,
                formattedTotalAmount,
                statusText: settlement.status.statusText,
                productType: settlement.productTypeName ?? '-',
              });
            });
            this.data = newData;
            this.totalItems = settlements.totalElements;
          });
          },
          error: (e: IpErrorResponse) => {
            this.ipSnackbarService.showErrorMessage(e);
          },
        });
        break;
      case 'settlements-details':
        this.subscription = this.route.paramMap
              .pipe(
                switchMap((params) => {
                  const selectedId = Number(params.get('id'));
                  return combineLatest({
                    currencies: this.ipCurrencyService.currencies$,
                    settlements: this.ipSettlementService.getDataBySupplierId(
                      this.supplierId = selectedId,
                      this.pagination,
                      this.sortFieldName,
                      this.sortOrder,
                      'description',
                      this.searchValue,
                      this.statusCode.toString()
                    ),
                  })
                })
              )
              .subscribe({
              next: ({ currencies, settlements }) => {
            if (!currencies) {
              return;
            }
            const newData: Array<SettlementRow> = [];
            settlements.content.forEach((settlement) => {
              const totalCommission =
                settlement.settlementSummary.totalCommission;
              const settlementCurrency =
                settlement.settlementSummary.settlementCurrency;
              const foundCurrency = currencies.find((currency) => {
                return currency.code === settlementCurrency;
              });
              let formattedTotalAmount = 'N/A';
              if (foundCurrency) {
                formattedTotalAmount = this.amountFormatPipe.transform(
                  totalCommission,
                  foundCurrency.isCommaFloat,
                  foundCurrency.symbol,
                  foundCurrency.code
                );
              } else {
                formattedTotalAmount = this.amountFormatPipe.transform(
                  totalCommission,
                  true,
                  '$',
                  settlementCurrency
                );
                console.warn(
                  "The settlement's currency wasn't found in the database."
                );
              }
              newData.push({
                ...settlement,
                formattedTotalAmount,
                statusText: settlement.status.statusText,
                productType: settlement.productTypeName ?? '-',
              });
            });
            this.data = newData;
            this.totalItems = settlements.totalElements;
          },
          error: (e: IpErrorResponse) => {
            this.ipSnackbarService.showErrorMessage(e);
          },
        });
        break;
      case 'brokers':
        this.cardConfig = {
          headerKey: 'businessName',
          fields: [
            { 
              label: this.translateService.instant('IP.CARD_TABLE.SUPPLIER.STATE'),
              key: 'userStatus', 
              primaryColorText: true,
              mapLabels: {
                'ACTIVATED': this.translateService.instant('IP.USER_FORM_MODAL.USER_STATE.ACTIVATED'),
                'DISABLED': this.translateService.instant('IP.USER_FORM_MODAL.USER_STATE.DISABLED'),
                'INACTIVE': this.translateService.instant('IP.USER_FORM_MODAL.USER_STATE.INACTIVE'),
              }
            },
            { 
              label: this.translateService.instant('IP.CARD_TABLE.SUPPLIER.USEREMAIL'),
              key: 'userEmail',
            },
            { 
              label: this.translateService.instant('IP.CARD_TABLE.SUPPLIER.USERCREATIONDATE'),
              key: 'userCreationDate',
              isDate: true
            },
            { 
              label: this.translateService.instant('IP.CARD_TABLE.SUPPLIER.LASTLOGINDATE'),
              key: 'lastLoginDate',
              isDate: true
            },
          ],
          showActionButton: true,
          actions: this.tableOption.actions,
          isActionDisabled: (item, action) => {
            if (action === 'delete' && item.status?.statusCode !== 'open') {
              return true;
            }
            return false;
          }
        };
        this.ipSupplierService.getBrokersWithPages(pageable, this.searchValueInput).subscribe({
          next: (data) => {
            const users: UserResponse[] = data.content;
            const supplierRows: SupplierRow[] = [];
            users.forEach((user) => {
                const newSupplierUser: SupplierRow = {
                  id: user.id,
                  userEmail: user.email,
                  username: user.fullName || user.username || '',
                  businessName: user.fullName || user.username || '',
                  state: user.userStatus === 'ACTIVATED',
                  userStatus: user.userStatus,
                  userCreationDate: user.userCreationDate,
                  lastLoginDate: user.lastLoginDate || '',
                  phoneNumber: user.phoneNumber,
                  avatar: user.avatar,
                  fiscalId: user.fiscalId
                };
                supplierRows.push(newSupplierUser);
            });
            this.data = supplierRows;
            this.totalItems = data.totalElements;
          },
          error: (e: IpErrorResponse) => {
            this.ipSnackbarService.showErrorMessage(e);
          },
        });
        break;
      case 'metrics-settlements-dashboard':
      case 'metrics-invoices-dashboard':
        this.data = (this.loadedData! as PageResponse<any>).content;
        this.totalItems = (this.loadedData! as PageResponse<any>).totalElements;
        this.currentPage = (this.loadedData! as PageResponse<any>).number;
        this.pageItems = (this.loadedData! as PageResponse<any>).size
        break;
      default:
        console.warn('No service defined.');
        this.data = [];
        break;
    }
  }

  openFiltersModal() {
    const dialogRef = this.dialog.open(FnFiltersModalComponent, {
      height: 'max-content',
      width: '90%',
      maxWidth: 'none',
      panelClass: 'custom-modal',
      data: {
        optionalTitle: this.optionalTitle,
        startDate: this.filterForm.get('creationDate')?.value ?? '',
        endDate: this.filterForm.get('dueDate')?.value ?? '',
        searchValue: this.searchValue ?? '',
        executeService: this.executeService.bind(this),
      },
    });

    dialogRef.disableClose = true;

    dialogRef.componentInstance.filtersApplied.subscribe((filters: any) => {
      this.filterForm.patchValue({
        creationDate: filters.startDate,
        dueDate: filters.endDate,
      });
      if (filters.searchValue !== '') {
        this.searchValue = filters.searchValue;
      } else {
        this.searchValue = '';
      }
      this.executeService(this.searchValue);
    });
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize(event: any) {
    this.updateMode(event.target.innerWidth);
  }
  
  private updateMode(windowWidth: number) {
    if (windowWidth >= this.changeWidth) {
      this.mode = 'side';
    } else {
      this.mode = 'over';
    }
  }

  convertToDate(completeDateString: string): Date | string {
    if (completeDateString) {
      const dateParts = completeDateString.split('T')
      const parts = dateParts[0].split('-');
      const year = parseInt(parts[0]);
      const month = parseInt(parts[1]) - 1;
      const day = parseInt(parts[2]);
      const partsHours = dateParts[1].split(':')
      const hours = parseInt(partsHours[0])
      const minutes = parseInt(partsHours[1])
      return new Date(year, month, day, hours, minutes);
    } else {
      return '';
    }
  }

  formatDate(date:Date) {
    const fechaISO = date.toISOString();
    return fechaISO.split('T')[0]; 
  }

  formatDateArg(date: Date){
    // const today = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
   return `${year}-${month}-${day}`;
  }

  firstDay(date:Date) {
    const newDate = new Date(date); 
    newDate.setDate(1);
    return newDate;
  }

  openDialog( ) {
    this.dialogFiltersRef = this.dialog.open(this.dialogTemplate);
  }

  onClose(){
    if(this.dialogFiltersRef){
      this.dialogFiltersRef.close()
    }
  }

  onSelect(ev: any) {
    this.selectedItemsChange.emit(ev)  
  }
  
  onSelectAll(ev: any) {
    this.selectedAllItems.emit(ev)  
  }

  private emitFilter(newFilters: FilterValues): void {
    if (this.filterKey) {
      this.filterCacheService.setFilter(this.filterKey, newFilters);
    }
  }

  private loadFilterFromCache(): void {
    if (this.filterKey && this.filterCacheService.exists(this.filterKey)) {
      const cachedFilter = this.filterCacheService.getFilter<FilterValues>(this.filterKey);
      if (cachedFilter) {
        this.filterForm.setValue({
          creationDate: cachedFilter.createDateFrom,
          dueDate: cachedFilter.createDateTo,
          originOwner: cachedFilter.originOwner,
          originBank: cachedFilter.originBank
        });
        this.filterValues.createDateFrom = cachedFilter.createDateFrom;
        this.filterValues.createDateTo = cachedFilter.createDateTo;
        this.filterValues.originOwner = cachedFilter.originOwner;
        this.filterValues.originBank = cachedFilter.originBank;
        
        this.emitFilter(cachedFilter);
      }
    }
  }
  
}
